/**
 * Daily notes + {{date}}-style templates (rendering itself lives in
 * @rbv/shared renderTemplate — this module wires it to the vault).
 */
import {
  DEFAULT_TEMPLATES_FOLDER,
  dailyNotePath,
  ensureMdExtension,
  normalizePath,
  renderTemplate,
  titleFromPath,
} from '@rbv/shared';
import { useApp } from '../store.js';
import { vault } from '../sync/vault.js';

export interface TemplateEntry {
  id: string;
  path: string;
  name: string;
}

/** Notes under the templates folder (settings.templatesFolder, default
 * "Templates"), sorted by name. */
export function listTemplates(): TemplateEntry[] {
  let folder = DEFAULT_TEMPLATES_FOLDER;
  try {
    folder = normalizePath(vault.settings.templatesFolder || DEFAULT_TEMPLATES_FOLDER);
  } catch {
    // vault not started — fall through with the default
  }
  const out: TemplateEntry[] = [];
  try {
    for (const [id, e] of vault.filesSnapshot()) {
      if (e.deleted || e.kind !== 'note') continue;
      if (!e.path.startsWith(folder + '/')) continue;
      out.push({ id, path: e.path, name: titleFromPath(e.path) });
    }
  } catch {
    return [];
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** Resolve a template path (with or without .md) and render it for a target
 * note. Returns '' when the template is unset or missing. */
function renderTemplateFor(templatePath: string | undefined, targetPath: string): string {
  if (!templatePath) return '';
  const id =
    vault.findByPath(templatePath) ?? vault.findByPath(ensureMdExtension(templatePath));
  if (!id) return '';
  return renderTemplate(vault.noteContent(id), {
    date: new Date(),
    title: titleFromPath(targetPath),
  });
}

/** Create a note at targetPath seeded from the rendered template; returns the
 * note id (the existing id if the path is already taken). */
export async function newNoteFromTemplate(
  templatePath: string,
  targetPath: string,
): Promise<string> {
  const path = ensureMdExtension(targetPath);
  const existing = vault.findByPath(path);
  if (existing) return existing;
  return vault.createNote(path, renderTemplateFor(templatePath, path));
}

/** Open today's daily note, creating it (seeded from settings.dailyTemplate,
 * if set) on first open of the day. */
export async function openDailyNote(): Promise<void> {
  const settings = vault.settings;
  const path = dailyNotePath(settings);
  let id = vault.findByPath(path);
  if (!id) {
    id = await vault.createNote(path, renderTemplateFor(settings.dailyTemplate, path));
  }
  useApp.getState().openNote(id);
}
